/**
 * RequireAuth — route guard that bounces unauthenticated users to /login.
 *
 * Wraps the protected layout (everything except /login). Renders a
 * lightweight skeleton while the AuthProvider is still doing its
 * initial `/auth/me` probe so the user doesn't see a flash of the
 * dashboard before being redirected.
 */

import { Loader2 } from "lucide-react";
import { Navigate, useLocation } from "react-router";

import { useAuth } from "../AuthContext";

interface Props {
  children: React.ReactNode;
}

export function RequireAuth({ children }: Props) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }

  if (status === "anonymous") {
    const next = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  return <>{children}</>;
}
